import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsBrush, BsFileEarmarkText, BsPencilFill } from 'react-icons/bs';

import SampleRecipientField from '#contactListFeature/components/SampleRecipientField/SampleRecipientField';
import { sampleRecipientRef } from '#contactListFeature/components/SampleRecipientField/sampleRecipientRef';
import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Select from '#globalComponents/Forms/Select';
import TextInput from '#globalComponents/Forms/TextInput';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import { useErrorStore } from '#lib/stores/errorStore';

import { generatePreviewPdf } from '../../../api/postMailingApi';
import { usePostMailingDetailsContext } from '../../../contexts/PostMailingDetailsContext';
import { openPdfBlob } from '../../../utils/openPdfBlob';

import PostMailingRecipientsCard from './PostMailingRecipientsCard';

const SettingsCard = ({ campaign, editable }) => {
	const { handleRename, handleTemplateChange, templates, loadingTemplates, selectedListIds } =
		usePostMailingDetailsContext();
	const navigate = useNavigate();
	const showError = useErrorStore((s) => s.showError);
	const [editing, setEditing] = useState(false);
	const [nameValue, setNameValue] = useState(campaign.name ?? '');
	const [changingTemplate, setChangingTemplate] = useState(false);
	const [pendingTemplateId, setPendingTemplateId] = useState(null);
	const [proofLoading, setProofLoading] = useState(false);
	const [proofRecipient, setProofRecipient] = useState(null);

	useEffect(() => {
		setNameValue(campaign.name ?? '');
	}, [campaign.name]);

	// The campaign links to a template via postMailingTemplateId; the design is read live from it.
	const currentTemplateId = campaign.postMailingTemplateId ?? '';
	const sourceTemplateName =
		campaign.postMailingTemplates?.name ?? templates.find((t) => t.id === currentTemplateId)?.name;

	// Render the final letter (sample or chosen recipient + the org's real profile) and open it in a new
	// tab, so the user can proof it. Requires a linked template.
	const handleProof = async () => {
		if (!currentTemplateId || proofLoading) return;
		setProofLoading(true);
		try {
			const blob = await generatePreviewPdf(campaign.id, sampleRecipientRef(proofRecipient));
			openPdfBlob(blob);
		} catch (err) {
			showError({ error: err, title: 'Beispiel-PDF konnte nicht erstellt werden' });
		} finally {
			setProofLoading(false);
		}
	};

	const applyTemplateChange = async (templateId) => {
		setPendingTemplateId(null);
		setChangingTemplate(true);
		try {
			await handleTemplateChange(templateId);
		} catch {
			// Error toast handled by the global MutationCache.
		}
		setChangingTemplate(false);
	};

	// Switching away from an already-linked template swaps the campaign's whole design — confirm first.
	const onTemplateSelect = (templateId) => {
		if (!templateId || templateId === currentTemplateId) return;
		if (currentTemplateId) {
			setPendingTemplateId(templateId);
			return;
		}
		applyTemplateChange(templateId);
	};

	const openTemplateEditor = () =>
		navigate(`/post-mailing/templates/${currentTemplateId}`, { state: { fromCampaignId: campaign.id } });

	const saveName = async () => {
		const trimmed = nameValue.trim();
		if (!trimmed || trimmed === campaign.name) {
			setEditing(false);
			return;
		}
		try {
			await handleRename(trimmed);
		} catch {
			// Error toast handled by the global MutationCache.
		}
		setEditing(false);
	};

	return (
		<Card>
			<h3 className="text-md font-semibold tracking-tight mb-4">Einstellungen</h3>

			<div className="space-y-5">
				{/* Name */}
				<div>
					<label className="block text-sm font-medium text-text mb-1">Kampagnenname</label>
					{editing && editable ? (
						<div className="flex items-center gap-2">
							<TextInput
								value={nameValue}
								onChange={(e) => setNameValue(e.target.value)}
								onKeyDown={(e) => e.key === 'Enter' && saveName()}
								className="flex-1"
								autoFocus
							/>
							<Button variant="outline" size="sm" onClick={saveName}>
								Speichern
							</Button>
							<Button variant="outline" size="sm" onClick={() => setEditing(false)}>
								Abbrechen
							</Button>
						</div>
					) : (
						<div className="flex items-center gap-2">
							<span className="text-lg font-semibold text-text">{campaign.name}</span>
							{editable && (
								<button
									onClick={() => setEditing(true)}
									className="text-bgBase-800 hover:text-text"
									title="Umbenennen"
								>
									<BsPencilFill className="h-4 w-4" />
								</button>
							)}
						</div>
					)}
				</div>

				{/* Letter template */}
				<div>
					<label className="block text-sm font-medium text-text mb-1">Brief-Vorlage auswählen</label>
					{editable ? (
						<>
							{loadingTemplates ? (
								<div className="text-sm text-bgBase-800">Vorlagen werden geladen...</div>
							) : (
								<Select
									value={currentTemplateId}
									onChange={(e) => onTemplateSelect(e.target.value)}
									disabled={changingTemplate}
									className="max-w-sm"
								>
									<option value="">Vorlage wählen...</option>
									{templates.map((tpl) => (
										<option key={tpl.id} value={tpl.id}>
											{tpl.name}
										</option>
									))}
								</Select>
							)}
							<p className="text-xs text-bgBase-800 mt-1">
								Die Kampagne verwendet immer den aktuellen Stand der verknüpften Vorlage — Änderungen an
								der Vorlage wirken sich bis zum Versand auch auf diese Kampagne aus.
							</p>
						</>
					) : sourceTemplateName ? (
						<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
							Basiert auf: {sourceTemplateName}
						</span>
					) : (
						<span className="text-sm text-bgBase-800">Kein Brief-Template hinterlegt.</span>
					)}

					{currentTemplateId && (
						<SampleRecipientField
							label="Beispiel-PDF für"
							recipient={proofRecipient}
							onChange={setProofRecipient}
							listIds={selectedListIds}
							disabled={proofLoading}
							className="mt-5 max-w-sm"
						/>
					)}

					{/* Jump into the template editor / open a sample of the final rendered letter */}
					<div className={`${currentTemplateId ? 'mt-3' : 'mt-5'} flex flex-wrap items-center gap-2`}>
						{editable && (
							<Button
								variant="outline"
								size="sm"
								onClick={openTemplateEditor}
								disabled={!currentTemplateId}
							>
								<BsBrush className="h-4 w-4" />
								Vorlage bearbeiten
							</Button>
						)}
						<Button
							variant="outline"
							size="sm"
							onClick={handleProof}
							disabled={!currentTemplateId || proofLoading}
							loading={proofLoading}
						>
							{!proofLoading && <BsFileEarmarkText className="h-4 w-4" />}
							{proofLoading ? 'Beispiel-PDF wird erstellt …' : 'Beispiel-PDF ansehen'}
						</Button>
					</div>
					{!currentTemplateId && (
						<p className="text-xs text-bgBase-800 mt-1">
							Bitte zuerst eine Brief-Vorlage auswählen, um sie zu bearbeiten oder eine Beispiel-PDF zu
							erstellen.
						</p>
					)}
				</div>
			</div>

			<ConfirmModal
				isOpen={Boolean(pendingTemplateId)}
				onClose={() => setPendingTemplateId(null)}
				onConfirm={() => applyTemplateChange(pendingTemplateId)}
				title="Vorlage wechseln"
				message={`Das Design dieser Kampagne wechselt zu "${templates.find((t) => t.id === pendingTemplateId)?.name ?? 'der gewählten Vorlage'}". Die bisherige Vorlage bleibt unverändert erhalten.`}
				confirmText="Vorlage wechseln"
				cancelText="Abbrechen"
				isLoading={changingTemplate}
			/>
		</Card>
	);
};

const PostMailingOverviewTab = () => {
	const { campaign, contactLists, selectedLists, handleListToggle } = usePostMailingDetailsContext();
	const [isEditingLists, setIsEditingLists] = useState(false);

	const editable = campaign.status === 'draft';

	return (
		<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
			<div className="lg:col-span-2 space-y-6">
				<SettingsCard campaign={campaign} editable={editable} />
			</div>
			<div className="lg:col-span-1">
				<PostMailingRecipientsCard
					selectedLists={selectedLists}
					allContactLists={contactLists}
					recipientCount={campaign.recipientCount ?? 0}
					isEditing={isEditingLists}
					setIsEditing={setIsEditingLists}
					onListToggle={handleListToggle}
					disabled={!editable}
				/>
			</div>
		</div>
	);
};

const campaignShape = PropTypes.object;

SettingsCard.propTypes = {
	campaign: campaignShape.isRequired,
	editable: PropTypes.bool.isRequired,
};

export default PostMailingOverviewTab;
